/* eslint-disable @typescript-eslint/no-explicit-any -- action/property objects come from JSON-cloned config
   tables whose shape varies per integration; see the DynamicAction/DynamicGroup/DynamicConfig note below. */
import { Injectable } from '@angular/core';
import { Account } from 'app/back-office/account.model';
import { AutomationHelpers } from 'app/automations/automation-helpers';
import {
  ACTIONS,
  ACTIONS_TYPES,
  ActionListField,
  ActionRealTimeScl,
  ActionUpdateField,
  ActionUpdateTags,
  ActionWithRealTime,
  ActionsPropertiesDetails,
  CONFIG_EDITOR_AUTOMATION,
  CreateRealTimeActions,
  PROPS_TYPE,
} from 'app/automations/automation-helpers-actions';
import { Automation, AutomationEditorConfig, TypeInput, TypeStep } from 'app/automations/automations-models';
import { IntegrationType } from 'app/automations/integrations-models';
import { AccountIdentifierConstants } from 'app/account/account-identifier-constants';
import { CampaignStatus } from 'app/campaign/campaign.model';
import WidgetAutomationHelpers from 'app/campaign/widget-automation-helpers';
import WidgetComponentHelpers from 'app/campaign/widget-component-helpers';
import { Widget, WidgetComponent, WidgetComponentSubType, WidgetState } from 'app/campaign/widgets.model';
import { LanguageService } from 'app/core/language.service';
import { fieldMappingPairs } from '../model/field-mapping-pairs';
import { isListPropName } from '../model/integration-capabilities';
import { TAG_PROP_NAME, isTriggerbeeTagAction } from '../model/tag-step-props';

export interface BuildAutomationRequestInput {
  account: Account;
  widgetState: WidgetState;
  widgetStates: WidgetState[];
  /** Clone-safe — it is not mutated. */
  automationConfig: AutomationEditorConfig;
  existingAutomation: Automation | null | undefined;
  campaignName: string;
  campaignStatus: CampaignStatus;
  runAutomationOnlyIfNewSubscriber: boolean;
  onlySubscriberCheck: boolean;
  removeVerifyNewSubscriberCheck?: boolean;
}

export interface BuiltAutomationRequest {
  automation: Automation;
  isUpdate: boolean;
  /** Zero means this save persists nothing for the app, however many actions the other integrations
   *  contribute to the same automation. */
  appActionCount: number;
}

type DynamicAction = any;
type DynamicGroup = any;
type DynamicConfig = any;

@Injectable()
export class AutomationSerializer {
  // eslint-disable-next-line @angular-eslint/prefer-inject
  constructor(private readonly languageService: LanguageService) {}

  public toEditorConfig(
    automation: Automation | null | undefined,
    widgetStates: WidgetState[],
    account: Account,
  ): AutomationEditorConfig[] {
    const configs: DynamicConfig[] = JSON.parse(JSON.stringify(CONFIG_EDITOR_AUTOMATION));
    this.applyRealTimePossibility(configs, automation, widgetStates, account);

    if (!automation) {
      configs.forEach((config) => this.initializeBlankConfig(config, widgetStates, account));
      return configs;
    }

    for (const config of configs) {
      // Scoped per config: a value latched from an earlier integration still passes the truthiness check
      // below, so the update groups would skip restoreListFromSavedUpdates and lose their saved list.
      let responseList: DynamicAction;
      for (const group of config.actionsConfig) {
        for (const item of group.actionsGroup) {
          const isUpdateField = ActionUpdateField.includes(item.action.$type as ACTIONS_TYPES);
          if (!isUpdateField) {
            responseList = this.hydrateSimpleAction(item, group, config, automation, responseList);
          } else if (group.nameGroup !== TypeStep.FieldMapping) {
            this.hydrateUpdateOtherFields(item, group, config, automation, widgetStates, account, responseList);
          } else {
            this.hydrateFieldMapping(item, group, config, automation, widgetStates, account, responseList);
          }
        }
      }
    }
    return configs;
  }

  private hydrateSimpleAction(
    item: DynamicGroup,
    group: DynamicGroup,
    config: DynamicConfig,
    automation: Automation,
    responseList: DynamicAction,
  ): DynamicAction {
    const actions = automation.actions ?? [];
    const responseAction = actions.find((action) => action.$type === item.action.$type);
    if (responseAction) {
      item.action = { ...item.action, ...responseAction };
      this.mirrorSavedListOntoProperties(item.action, responseAction);
      this.mirrorSavedTagOntoProperties(item.action, responseAction);
      config.enabled = true;
      if (ActionListField.includes(responseAction.$type as ACTIONS_TYPES)) {
        responseList = responseAction;
      }
    } else {
      item.action = { ...item.action, ...this.emptyPropertyValues(item.action.$type) };
    }
    if (group.requiredList) {
      const matchingList = actions.find(
        (action) =>
          ActionListField.includes(action.$type as ACTIONS_TYPES) && action.$type.includes(item.action.nameAction),
      );
      if (matchingList) {
        this.setListIdOnOtherAction(item.action, matchingList);
      } else {
        this.restoreListFromSavedUpdates(item.action, this.savedUpdateActionsOf(actions, item.action.nameAction));
      }
    }
    return responseList;
  }

  private hydrateUpdateOtherFields(
    item: DynamicGroup,
    group: DynamicGroup,
    config: DynamicConfig,
    automation: Automation,
    widgetStates: WidgetState[],
    account: Account,
    responseList: DynamicAction,
  ): void {
    const actions = automation.actions ?? [];
    const matching = actions.filter((action) => action.$type === item.action.$type);
    const otherFields = matching.filter((action) => action.fieldValue && !this.isFormMergeVar(action.fieldValue));
    const formFields = matching.filter((action) => action.fieldValue && this.isFormMergeVar(action.fieldValue));

    // Form-mapped values that no longer correspond to a current widget input are treated as "other fields".
    if (formFields.length && widgetStates?.length) {
      const inputMergeVars = new Set(
        this.getInputComponents(widgetStates, account).map((input) =>
          WidgetAutomationHelpers.getAutomationActionFieldValueMergevar(input),
        ),
      );
      otherFields.push(...formFields.filter((action) => !inputMergeVars.has(action.fieldValue)));
    }

    if (responseList && (responseList.listId || responseList.tagIds)) {
      this.setListIdOnOtherAction(item.action, responseList);
    } else {
      this.restoreListFromSavedUpdates(item.action, matching);
    }

    if (!otherFields.length) {
      return;
    }
    const properties: DynamicAction[] = [];
    for (const action of otherFields) {
      properties.push({ value: action.fieldValue, ...this.propertyDetail(action.$type, PROPS_TYPE.FieldValue) });
      properties.push({ value: action.fieldName, ...this.propertyDetail(action.$type, PROPS_TYPE.FieldName) });
    }
    group.actionsGroup[0].action = {
      fieldValue: '',
      fieldName: '',
      ...group.actionsGroup[0].action,
      properties,
    };
    config.enabled = true;
  }

  private hydrateFieldMapping(
    item: DynamicGroup,
    group: DynamicGroup,
    config: DynamicConfig,
    automation: Automation,
    widgetStates: WidgetState[],
    account: Account,
    responseList: DynamicAction,
  ): void {
    const actions = automation.actions ?? [];
    const matching = actions.filter((action) => action.$type === item.action.$type);
    const formFields = matching.filter((action) => action.fieldValue && this.isFormMergeVar(action.fieldValue));

    if (responseList && (responseList.listId || responseList.tagIds)) {
      this.setListIdOnOtherAction(item.action, responseList);
    } else {
      this.restoreListFromSavedUpdates(item.action, matching);
    }

    const properties: DynamicAction[] = [];
    for (const input of this.getInputComponents(widgetStates, account)) {
      const mergeVar = WidgetAutomationHelpers.getAutomationActionFieldValueMergevar(input);
      const savedMapping = formFields.find((action) => action.fieldValue === mergeVar);
      const fieldValue = savedMapping ? savedMapping.fieldValue : mergeVar;
      const fieldName = savedMapping ? savedMapping.fieldName : item.action.fieldName;
      const sourceType = savedMapping ? savedMapping.$type : item.action.$type;

      if (!WidgetAutomationHelpers.checkVoyadoRealTimeProp(fieldValue, fieldName, input, config, automation)) {
        continue;
      }
      properties.push({
        value: fieldValue,
        ...this.propertyDetail(sourceType, PROPS_TYPE.FieldValue),
        mapName: WidgetAutomationHelpers.getNameOfField(input, this.languageService),
        isSocialInput: WidgetAutomationHelpers.getSocialSecurityInput(input),
      });
      properties.push({ value: fieldName, ...this.propertyDetail(sourceType, PROPS_TYPE.FieldName) });
    }

    group.actionsGroup[0].action = {
      fieldValue: '',
      fieldName: '',
      ...group.actionsGroup[0].action,
      properties,
    };
    // Only a mapping that actually had saved form-field values flips the integration to "configured".
    if (formFields.length) {
      config.enabled = true;
    }
  }

  private initializeBlankConfig(config: DynamicConfig, widgetStates: WidgetState[], account: Account): void {
    config.actionsConfig.forEach((group: DynamicGroup) => {
      group.actionsGroup.forEach((item: DynamicGroup) => {
        if (group.nameGroup !== TypeStep.FieldMapping) {
          item.action = { ...item.action, ...this.emptyPropertyValues(item.action.$type) };
          return;
        }
        const properties: DynamicAction[] = [];
        this.getInputComponents(widgetStates, account).forEach((input) => {
          properties.push({
            value: WidgetAutomationHelpers.getAutomationActionFieldValueMergevar(input),
            ...this.propertyDetail(item.action.$type, PROPS_TYPE.FieldValue),
            mapName: WidgetAutomationHelpers.getNameOfField(input, this.languageService),
            isSocialInput: WidgetAutomationHelpers.getSocialSecurityInput(input),
          });
          properties.push({ value: '', ...this.propertyDetail(item.action.$type, PROPS_TYPE.FieldName) });
        });
        item.action = { ...item.action, ...this.emptyPropertyValues(item.action.$type) };
        item.action.properties = properties;
      });
    });
  }

  // The form-settings card validates `prop.value`, which the per-action editor only fills in once it renders.
  private mirrorSavedListOntoProperties(action: DynamicAction, saved: DynamicAction): void {
    (action.properties ?? []).forEach((prop: DynamicAction) => {
      if (isListPropName(prop.name) && this.hasListValue(saved[prop.name])) {
        prop.value = saved[prop.name];
      }
    });
  }

  // Tag steps render their own picker rather than the per-action editor that normally fills `prop.value`,
  // and the save path serializes `properties` alone: without this a re-save drops the profile switch.
  private mirrorSavedTagOntoProperties(action: DynamicAction, saved: DynamicAction): void {
    if (!isTriggerbeeTagAction(action)) {
      return;
    }
    (action.properties ?? []).forEach((prop: DynamicAction) => {
      if (prop.name === TAG_PROP_NAME) {
        prop.value = saved.tag ?? null;
      } else if (prop.typeInput === TypeInput.Switch) {
        // Strict, so a saved `false` stays false and a missing key never lands as undefined.
        prop.value = saved[prop.name] === true;
      }
    });
  }

  public setListIdOnOtherAction(action: DynamicAction, response: DynamicAction): void {
    const nameAutomation = action.nameAction === 'MailChimp' ? 'Mailchimp' : action.nameAction;
    if (!response.$type.includes(nameAutomation)) {
      return;
    }
    const propListName = action.nameAction === 'Rule' ? PROPS_TYPE.TagIds : PROPS_TYPE.ListId;
    if ((response.listId || response.tagIds) && action.requiredList) {
      action[propListName] = response.listId || response.tagIds;
    }
  }

  // An automation saved in "update contact" mode has no create action to carry the list forward, so the list
  // survives only on the persisted update actions themselves.
  private restoreListFromSavedUpdates(action: DynamicAction, savedActions: DynamicAction[]): void {
    if (!action.requiredList) {
      return;
    }
    const propListName = action.nameAction === 'Rule' ? PROPS_TYPE.TagIds : PROPS_TYPE.ListId;
    const saved = savedActions.find((candidate) => this.hasListValue(candidate[propListName]));
    if (saved) {
      action[propListName] = saved[propListName];
    }
  }

  private savedUpdateActionsOf(actions: DynamicAction[], nameAction: string): DynamicAction[] {
    return actions.filter(
      (action) => ActionUpdateField.includes(action.$type as ACTIONS_TYPES) && action.$type.includes(nameAction),
    );
  }

  private hasListValue(value: DynamicAction): boolean {
    return Array.isArray(value) ? value.length > 0 : !!value;
  }

  public buildAutomationRequest(input: BuildAutomationRequestInput): BuiltAutomationRequest {
    const {
      account,
      widgetState,
      widgetStates,
      existingAutomation,
      campaignName,
      campaignStatus,
      runAutomationOnlyIfNewSubscriber,
      onlySubscriberCheck,
      removeVerifyNewSubscriberCheck = false,
    } = input;
    // Clone so we never mutate the caller's editor config while flattening property values onto actions.
    const automationConfig: DynamicConfig = JSON.parse(JSON.stringify(input.automationConfig));

    const actionsOnAutomation = this.serializeConfiguredActions(automationConfig);

    let automation = this.mergeWithExisting(
      automationConfig,
      existingAutomation,
      widgetState,
      actionsOnAutomation,
      account,
      campaignName,
    );
    automation.actions = automation.actions?.length ? this.excludeEmptyTagActions(automation.actions) : [];
    automation.isDisabled = campaignStatus !== CampaignStatus.Published;
    // Before applyNewSubscriberTrigger, which may hand back a different automation object. Identity, not
    // $type: an app can share a $type with an action it did not produce this round.
    const appActionCount = automation.actions.filter((action) => actionsOnAutomation.includes(action)).length;

    automation = this.applyNewSubscriberTrigger(
      automation,
      automationConfig,
      existingAutomation,
      runAutomationOnlyIfNewSubscriber,
      onlySubscriberCheck,
      removeVerifyNewSubscriberCheck,
    );
    this.markRealTimeActions(automation, widgetStates, account);

    const isUpdate = !!(
      existingAutomation &&
      widgetState.automationId &&
      widgetState.automationId === existingAutomation.id
    );
    return { automation, isUpdate, appActionCount };
  }

  private serializeConfiguredActions(automationConfig: DynamicConfig): DynamicAction[] {
    const actions: DynamicAction[] = [];
    const createAction = this.findCreateRealTimeAction(automationConfig);
    const createListProp = createAction ? AutomationHelpers.getNamePropForCreateAction(createAction) : undefined;
    // The create action's list/tag value decides whether field mappings fold into its `fieldMappings`
    // (real-time path) or serialize as separate update actions.
    const createListValue = this.createActionListValue(createAction, createListProp);

    automationConfig.actionsConfig.forEach((group: DynamicGroup) => {
      group.actionsGroup.forEach((actionGroup: DynamicGroup) => {
        const isUpdateField = ActionUpdateField.includes(actionGroup.action.$type as ACTIONS_TYPES);
        if (!isUpdateField) {
          this.serializeSimpleAction(actionGroup, actions);
        } else if (this.shouldSerializeAsSeparateUpdates(group, automationConfig, createListValue, createListProp)) {
          this.serializeUpdateFieldActions(actionGroup, automationConfig, actions);
        } else if (this.shouldFoldIntoRealTimeMappings(group, automationConfig, createListValue, createListProp)) {
          if (!this.foldFieldMappingsIntoCreateAction(actionGroup, actions)) {
            this.serializeUpdateFieldActions(actionGroup, automationConfig, actions);
          }
        }
      });
    });
    return actions;
  }

  private findCreateRealTimeAction(automationConfig: DynamicConfig): DynamicAction {
    const addGroup = automationConfig.actionsConfig.find(
      (group: DynamicGroup) => group.nameGroup === TypeStep.AddContact,
    );
    return addGroup?.actionsGroup.find((actionGroup: DynamicGroup) =>
      CreateRealTimeActions.includes(actionGroup?.action?.$type as ACTIONS_TYPES),
    );
  }

  // The create action's configured list/tag value lives in its un-flattened `properties` array; fall back to
  // a flattened field if one is already present. Returns the raw value so an empty multi-select array still
  // counts as "has a list".
  private createActionListValue(createAction: DynamicAction, createListProp: string | null | undefined): DynamicAction {
    if (!createAction || createListProp === null || createListProp === undefined) {
      return undefined;
    }
    const action = createAction.action;
    const prop = (action.properties ?? []).find((property: DynamicAction) => property.name === createListProp);
    return prop ? prop.value : action[createListProp];
  }

  private serializeSimpleAction(actionGroup: DynamicGroup, actions: DynamicAction[]): void {
    const action: DynamicAction = actionGroup.action;
    const isCleverReachAdd = action.$type === ACTIONS_TYPES.CleverReachCreateContact;
    const isCustomSendWebhook = action.$type === ACTIONS_TYPES.CustomSendWebhook;
    const hasAnyValue = action.properties.some(
      (prop: DynamicAction) => prop.value || prop.value === false || prop.typeInput === TypeInput.Description,
    );
    if (!isCleverReachAdd && !isCustomSendWebhook && !hasAnyValue) {
      return;
    }

    const propListName = action.nameAction === 'Rule' ? PROPS_TYPE.TagIds : PROPS_TYPE.ListId;
    const listId = action[propListName];

    const hasMeaningfulValue = action.properties.some(
      (prop: DynamicAction) =>
        (!Array.isArray(prop.value) && prop.value) ||
        prop.value === false ||
        prop.typeInput === TypeInput.Description ||
        (Array.isArray(prop.value) && prop.value.some((value: DynamicAction) => value)),
    );
    if (!isCleverReachAdd && !isCustomSendWebhook && !hasMeaningfulValue) {
      return;
    }

    const target: DynamicAction = { $type: action.$type, ...this.flattenProperties(action.properties) };
    if (target.$type === ACTIONS_TYPES.VoyadoUpdateConsentPreferences) {
      Object.keys(target)
        .filter((key) => key !== '$type')
        .forEach((key) => {
          if (target[key] === 'none') {
            target[key] = null;
          }
        });
    }
    if (action.requiredList) {
      target.listId = listId;
    }
    if (!action.properties.some((prop: DynamicAction) => prop.requiredInFormSettings && prop.value === '')) {
      actions.push(target);
    }
  }

  private shouldSerializeAsSeparateUpdates(
    group: DynamicGroup,
    automationConfig: DynamicConfig,
    createListValue: DynamicAction,
    createListProp: string | null | undefined,
  ): boolean {
    if (group.nameGroup === TypeStep.UpdateOtherFields) {
      return true;
    }
    if (group.nameGroup !== TypeStep.FieldMapping) {
      return false;
    }
    if (!automationConfig.isFormMappingProp) {
      return true;
    }
    return !createListValue && createListProp !== null;
  }

  private shouldFoldIntoRealTimeMappings(
    group: DynamicGroup,
    automationConfig: DynamicConfig,
    createListValue: DynamicAction,
    createListProp: string | null | undefined,
  ): boolean {
    return (
      group.nameGroup === TypeStep.FieldMapping &&
      !!automationConfig.isFormMappingProp &&
      (!!createListValue || createListProp === null)
    );
  }

  private serializeUpdateFieldActions(
    actionGroup: DynamicGroup,
    automationConfig: DynamicConfig,
    actions: DynamicAction[],
  ): void {
    const action: DynamicAction = actionGroup.action;
    const propListName =
      action.$type === ACTIONS_TYPES.RuleMailerUpdateMergeTag ? PROPS_TYPE.TagIds : PROPS_TYPE.ListId;
    const carriesList =
      action.requiredList || action.automationHasActionRealTime || automationConfig.isRunningInRealTime;
    // setListIdOnFieldProperty mirrors the list onto the action and onto its non-list properties; the
    // property copy outlives the action field in some flows.
    const listPayload: Record<string, DynamicAction> = carriesList
      ? { [propListName]: action[propListName] || this.listStampedOnProperties(action.properties, propListName) }
      : {};

    fieldMappingPairs(action.properties).forEach((pair) => {
      actions.push({
        $type: action.$type,
        ...listPayload,
        [pair.first.name]: pair.first.value,
        [pair.second.name]: pair.second.value,
      });
    });
  }

  private listStampedOnProperties(properties: DynamicAction[], propListName: string): DynamicAction {
    return (properties ?? []).find((prop: DynamicAction) => prop[propListName])?.[propListName];
  }

  private foldFieldMappingsIntoCreateAction(actionGroup: DynamicGroup, actions: DynamicAction[]): boolean {
    const mappings: Record<string, DynamicAction> = {};
    const properties: DynamicAction[] = actionGroup.action.properties;
    properties.forEach((prop: DynamicAction, index: number) => {
      const nextProp = properties[index + 1];
      if (
        prop.name === PROPS_TYPE.FieldValue &&
        prop.value &&
        nextProp?.name === PROPS_TYPE.FieldName &&
        nextProp.value
      ) {
        WidgetAutomationHelpers.replaceFieldMapKey(prop, this.languageService);
        mappings[prop.mapName] = nextProp.value;
      }
    });
    if (!Object.values(mappings).length) {
      return false;
    }
    const createAction = actions.find((action) => CreateRealTimeActions.includes(action.$type));
    if (!createAction) {
      return false;
    }
    createAction.fieldMappings = mappings;
    return true;
  }

  private mergeWithExisting(
    automationConfig: DynamicConfig,
    existingAutomation: Automation | null | undefined,
    widgetState: WidgetState,
    actionsOnAutomation: DynamicAction[],
    account: Account,
    campaignName: string,
  ): Automation {
    const hasInvalidReference = !!widgetState.automationId && !existingAutomation;

    if (!widgetState.automationId || hasInvalidReference) {
      const automation = WidgetAutomationHelpers.generateBlankWidgetAutomation(
        account,
        {} as Automation,
        widgetState.widgetId,
      );
      automation.actions = actionsOnAutomation;
      this.generateAutomationName(campaignName, automation);
      return automation;
    }
    if (existingAutomation && existingAutomation.actions?.length) {
      const otherAppActions = existingAutomation.actions.filter((action) =>
        this.belongsToOtherApp(action, automationConfig, actionsOnAutomation),
      );
      return { ...existingAutomation, actions: [...otherAppActions, ...actionsOnAutomation] };
    }
    if (existingAutomation && !existingAutomation.actions?.length) {
      return { ...existingAutomation, actions: actionsOnAutomation };
    }
    return {} as Automation;
  }

  // Keep actions belonging to other integrations (or real-time SCL actions) when re-saving one app.
  private belongsToOtherApp(
    action: DynamicAction,
    automationConfig: DynamicConfig,
    actionsOnAutomation: DynamicAction[],
  ): boolean {
    const nameAutomation = automationConfig.name === 'MailChimp' ? 'Mailchimp' : automationConfig.name;
    if (automationConfig.type === 'step') {
      return (
        !actionsOnAutomation.map((item) => item.$type).includes(action.$type) ||
        (ActionRealTimeScl as readonly string[]).includes(action.$type)
      );
    }
    return !action.$type.includes(nameAutomation) || (ActionRealTimeScl as readonly string[]).includes(action.$type);
  }

  private applyNewSubscriberTrigger(
    automation: Automation,
    automationConfig: DynamicConfig,
    existingAutomation: Automation | null | undefined,
    runAutomationOnlyIfNewSubscriber: boolean,
    onlySubscriberCheck: boolean,
    removeVerifyNewSubscriberCheck: boolean,
  ): Automation {
    const realTimeAutomationAdded = AutomationHelpers.isBrevoDotVoyadoRuleRealTime(automationConfig);
    let result = automation;
    if (onlySubscriberCheck || realTimeAutomationAdded) {
      if (runAutomationOnlyIfNewSubscriber === true || realTimeAutomationAdded) {
        result = this.ensureOnlyNewSubscriber(realTimeAutomationAdded ? automation : existingAutomation);
      } else if (runAutomationOnlyIfNewSubscriber === false) {
        result = this.ensureNotOnlyNewSubscriber(existingAutomation);
      }
    }
    if (removeVerifyNewSubscriberCheck) {
      result = this.ensureNotOnlyNewSubscriber(result);
    }
    return result;
  }

  private markRealTimeActions(automation: Automation, widgetStates: WidgetState[], account: Account): void {
    automation.actions.forEach((action) => {
      const nameAction = this.realTimeIntegrationFor(action.$type);
      if (
        nameAction &&
        this.isActionCanBeWithRealTime(widgetStates, nameAction, account) &&
        ActionWithRealTime.includes(action.$type as ACTIONS_TYPES)
      ) {
        action.isRunningInRealTime = true;
      }
    });
  }

  private realTimeIntegrationFor(type: string): string | undefined {
    switch (true) {
      case type.includes(IntegrationType.Rule):
        return IntegrationType.Rule;
      case type.includes(IntegrationType.Klaviyo):
        return IntegrationType.Klaviyo;
      case type.includes(IntegrationType.Custobar):
        return IntegrationType.Custobar;
      case type === ACTIONS_TYPES.BrevoCreateContact:
        return IntegrationType.Brevo;
      case type === ACTIONS_TYPES.OmnisendCreateContact:
        return IntegrationType.Omnisend;
      case type === ACTIONS_TYPES.CleverReachCreateContact:
        return IntegrationType.CleverReach;
      case type === ACTIONS_TYPES.DotdigitalCreateContact:
        return IntegrationType.Dotdigital;
      case type === ACTIONS_TYPES.ShopwareCreateMember:
        return IntegrationType.Shopware;
      default:
        return undefined;
    }
  }

  public generateAutomationName(campaignName: string, automation: Automation): void {
    automation.name =
      campaignName === null
        ? this.languageService.translateInstantly('campaign.widget.sidebar.form-settings.gen-automation-unnamed-name')
        : this.languageService.translateInstantly('campaign.widget.sidebar.form-settings.gen-automation-name', {
            campaignName,
          });
  }

  public excludeEmptyTagActions(actions: DynamicAction[]): DynamicAction[] {
    return actions.filter((action) => {
      if (ActionUpdateTags.includes(action.$type)) {
        if (Array.isArray(action.tags) && action.tags.some((tag: string) => tag.trim())) {
          action.tags = action.tags.filter((tag: string) => tag.trim());
          return true;
        }
        return false;
      }
      if (action.$type === ACTIONS_TYPES.TriggerbeeRemoveTag || action.$type === ACTIONS_TYPES.TriggerbeeAddTag) {
        return !!action.tag;
      }
      return true;
    });
  }

  // Clones first so a failed save never half-mutates the caller's automation.
  public setNewSubscriberOnly(automation: Automation | null | undefined, enabled: boolean): Automation {
    const clone: Automation = JSON.parse(JSON.stringify(automation ?? {}));
    return enabled ? this.ensureOnlyNewSubscriber(clone) : this.ensureNotOnlyNewSubscriber(clone);
  }

  private ensureOnlyNewSubscriber(automation: Automation | null | undefined): Automation {
    if (automation?.triggers) {
      const index = automation.triggers.findIndex((trigger) => trigger.type === 'Tags');
      if (index !== -1) {
        if (automation.triggers[index].argument !== 'NewSubscriber') {
          automation.triggers.splice(index, 1);
        }
      } else {
        automation.triggers.push(WidgetAutomationHelpers.getDefaultNewSubscriberTrigger());
      }
    }
    return automation as Automation;
  }

  private ensureNotOnlyNewSubscriber(automation: Automation | null | undefined): Automation {
    if (automation?.triggers) {
      const index = automation.triggers.findIndex((trigger) => trigger.type === 'Tags');
      if (
        index !== -1 &&
        automation.triggers[index].argument === 'NewSubscriber' &&
        automation.triggers[index].operator === 'Equals'
      ) {
        automation.triggers.splice(index, 1);
      }
    }
    return automation as Automation;
  }

  // ESPs that always support real time short-circuit; otherwise requires a subscription-checkbox component
  // wired to the same integration plus an identifier input.
  public isActionCanBeWithRealTime(
    widgetStates: WidgetState[] | { states: WidgetState[] },
    nameAction: string,
    account: Account,
    isRealTimeAction?: boolean,
  ): string | undefined {
    if (
      nameAction === IntegrationType.Brevo ||
      nameAction === IntegrationType.CleverReach ||
      nameAction === IntegrationType.Dotdigital ||
      nameAction === IntegrationType.Omnisend ||
      nameAction === IntegrationType.Shopware
    ) {
      return nameAction;
    }
    const widget = ('states' in widgetStates ? widgetStates : { states: widgetStates }) as Widget;
    const subscriptionCheckboxes = WidgetComponentHelpers.getAllComponentsOfTypes(
      null,
      WidgetComponentHelpers.getAllComponentsOnWidget(widget),
      [WidgetComponentSubType.ButtonListSubscriptionCheckbox],
    );
    const identifierInputs = WidgetComponentHelpers.getAllComponentsOfTypes(
      null,
      WidgetComponentHelpers.getAllComponentsOnWidget(widget),
      this.identifierInputSubTypes(account.identifier),
    );
    const sourceMatches =
      subscriptionCheckboxes[0]?.subscriptionCheckboxProperties?.sourceIntegration?.toLowerCase() ===
      nameAction?.toLowerCase();

    if (nameAction) {
      if (!isRealTimeAction && subscriptionCheckboxes?.length && identifierInputs?.length && sourceMatches) {
        return nameAction;
      }
      if (isRealTimeAction && subscriptionCheckboxes?.length && sourceMatches) {
        return nameAction;
      }
      return undefined;
    }
    if (subscriptionCheckboxes?.length && identifierInputs?.length) {
      return subscriptionCheckboxes[0].subscriptionCheckboxProperties?.sourceIntegration;
    }
    return undefined;
  }

  private identifierInputSubTypes(identifier: string): WidgetComponentSubType[] {
    return identifier === AccountIdentifierConstants.Telephone
      ? [WidgetComponentSubType.InputTextPhone, WidgetComponentSubType.InternationalPhoneRegular]
      : [WidgetComponentSubType.InputTextEmail];
  }

  private applyRealTimePossibility(
    configs: DynamicConfig[],
    automation: Automation | null | undefined,
    widgetStates: WidgetState[],
    account: Account,
  ): void {
    configs.forEach((config: DynamicConfig) => {
      config.actionsConfig.forEach((group: DynamicGroup) => {
        group.actionsGroup.forEach((item: DynamicGroup) => {
          const savedAction = automation?.actions?.find((action) => action.$type === item.action.$type);
          const savedRealTimeScl = automation?.actions?.find(
            (action) =>
              (ActionRealTimeScl as readonly string[]).includes(action.$type) &&
              action.$type.includes(item.action.nameAction),
          );
          if (savedRealTimeScl) {
            item.action.automationHasActionRealTime = true;
          }
          const runsInRealTime = (savedAction && savedAction.isRunningInRealTime) || savedRealTimeScl;
          const canRunInRealTime =
            (!automation || !savedAction?.isRunningInRealTime) &&
            this.isActionCanBeWithRealTime(widgetStates, item.action.nameAction, account);
          if (runsInRealTime || canRunInRealTime) {
            this.excludeRealTimeProperties(item, group, config, widgetStates, account);
          }
        });
      });
    });
  }

  private excludeRealTimeProperties(
    item: DynamicGroup,
    group: DynamicGroup,
    config: DynamicConfig,
    widgetStates: WidgetState[],
    account: Account,
  ): void {
    group.requiredList = false;
    item.action.requiredList = false;
    if (!item.action.properties?.length) {
      return;
    }
    item.action.properties.forEach((prop: DynamicAction) => {
      prop.requiredList = false;
      if (prop.name === PROPS_TYPE.TagIds || prop.name === PROPS_TYPE.ListId) {
        prop.required = false;
      }
    });
    if (!ActionWithRealTime.includes(item.action.$type as ACTIONS_TYPES) && !item.action.updatedAction) {
      return;
    }
    item.action.properties.forEach((prop: DynamicAction) => {
      if (prop.name !== PROPS_TYPE.TagIds && prop.name !== PROPS_TYPE.ListId) {
        return;
      }
      prop.isRunningInRealTime = true;
      config.isRunningInRealTime = true;
      prop.required = false;
      prop.requiredInFormSettings = false;
      if (
        !WidgetAutomationHelpers.addExtraTagForRunningInRealTime(item.action, prop, widgetStates) &&
        this.isActionCanBeWithRealTime(widgetStates, item.action.nameAction, account, true)
      ) {
        prop.typeInput = TypeInput.Description;
      }
    });
  }

  private getInputComponents(widgetStates: WidgetState[], account: Account): WidgetComponent[] {
    const identifierType =
      account && account.identifier === 'telephone'
        ? WidgetComponentSubType.InputTextPhone
        : WidgetComponentSubType.InputTextEmail;
    return WidgetComponentHelpers.extractInputFieldComponentsFromState(
      widgetStates,
      [WidgetComponentSubType.InputTextPassword, WidgetComponentSubType.ButtonListSubscriptionCheckbox],
      true,
    ).filter((input) => input.subType !== identifierType);
  }

  private isFormMergeVar(fieldValue: string): boolean {
    return fieldValue.startsWith('{{session.custom.') && fieldValue.endsWith('}}');
  }

  private emptyPropertyValues(type: string): Record<string, string> {
    const definition = ACTIONS.find((action) => action.$type === type);
    return Object.fromEntries((definition?.properties ?? []).map((prop: DynamicAction) => [prop, '']));
  }

  private propertyDetail(type: string, propName: string): DynamicAction {
    return ActionsPropertiesDetails[type].properties.find((prop: DynamicAction) => prop.name === propName);
  }

  private flattenProperties(properties: DynamicAction[]): Record<string, DynamicAction> {
    return Object.fromEntries(properties.map((prop) => [prop.name, prop.value]));
  }
}
